import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileCode, Copy, Plus, Trash2, Play, AlertTriangle, CheckCircle, 
  XCircle, RefreshCw, Settings, Code, Database, Shield, Zap,
  ChevronDown, ChevronRight, Terminal, Bug, Wrench
} from 'lucide-react';
import { toast } from 'sonner';

// Sigma to KQL field mappings
const SIGMA_TO_KQL_FIELDS = {
  // Windows Security Events
  'EventID': 'EventID',
  'CommandLine': 'CommandLine',
  'ParentCommandLine': 'ParentCommandLine',
  'Image': 'NewProcessName',
  'ParentImage': 'ParentProcessName',
  'User': 'SubjectUserName',
  'TargetUserName': 'TargetUserName',
  'LogonType': 'LogonType',
  'SourceIp': 'IpAddress',
  'DestinationIp': 'DestinationIP',
  'DestinationPort': 'DestinationPort',
  'ProcessId': 'NewProcessId',
  'TargetFilename': 'ObjectName',
  'Hashes': 'Hashes',
  'md5': 'MD5',
  'sha256': 'SHA256',
  'sha1': 'SHA1',
  // Sysmon specific
  'OriginalFileName': 'OriginalFileName',
  'CurrentDirectory': 'CurrentDirectory',
  'IntegrityLevel': 'IntegrityLevel',
  'Product': 'Product',
  'Company': 'Company',
  'Description': 'Description',
  // Network
  'DestinationHostname': 'DestinationHostname',
  'SourcePort': 'SourcePort',
  'Protocol': 'Protocol',
  // Registry
  'TargetObject': 'TargetObject',
  'Details': 'Details',
  // DNS
  'QueryName': 'QueryName',
  'QueryType': 'QueryType',
  'QueryResults': 'QueryResults',
};

// KQL Table mappings by log source
const LOGSOURCE_TO_KQL_TABLE = {
  'windows-security': 'SecurityEvent',
  'windows-sysmon': 'SysmonEvent',
  'windows-powershell': 'Event',
  'windows-system': 'Event',
  'linux-syslog': 'Syslog',
  'linux-auditd': 'AuditLog',
  'firewall': 'CommonSecurityLog',
  'proxy': 'CommonSecurityLog',
  'webserver': 'W3CIISLog',
  'dns': 'DnsEvents',
  'azure-activitylogs': 'AzureActivity',
  'azure-signin': 'SigninLogs',
  'aws-cloudtrail': 'AWSCloudTrail',
  'gcp-audit': 'GCPAuditLogs',
};

// Preflight checks
const PREFLIGHT_CHECKS = [
  { id: 'title', name: 'Rule Title', description: 'Rule must have a descriptive title', required: true },
  { id: 'description', name: 'Description', description: 'Rule should have a description', required: false },
  { id: 'author', name: 'Author', description: 'Rule should have an author', required: false },
  { id: 'logsource', name: 'Log Source', description: 'Log source must be defined', required: true },
  { id: 'detection', name: 'Detection Logic', description: 'At least one detection field required', required: true },
  { id: 'condition', name: 'Condition', description: 'Detection condition must be valid', required: true },
  { id: 'level', name: 'Severity Level', description: 'Rule severity should be set', required: false },
  { id: 'falsepositives', name: 'False Positives', description: 'Document known false positives', required: false },
];

const SigmaRules = () => {
  const [rule, setRule] = useState({
    title: '',
    description: '',
    author: '',
    references: [''],
    tags: ['attack.execution'],
    logsource: { 
      product: 'windows', 
      service: 'security',
      category: ''
    },
    detection: {
      selection: [
        { field: 'EventID', modifier: '', value: '4624' }
      ],
      filter: [],
      condition: 'selection'
    },
    falsepositives: ['Unknown'],
    level: 'medium',
    status: 'experimental'
  });
  
  const [generatedSigma, setGeneratedSigma] = useState('');
  const [generatedKQL, setGeneratedKQL] = useState('');
  const [generatedSplunk, setGeneratedSplunk] = useState('');
  const [preflightResults, setPreflightResults] = useState([]);
  const [showPreflight, setShowPreflight] = useState(false);
  const [troubleshootResults, setTroubleshootResults] = useState([]);
  const [activeTab, setActiveTab] = useState('sigma');

  // Run preflight checks
  const runPreflightChecks = () => {
    const results = PREFLIGHT_CHECKS.map(check => {
      let passed = false;
      let message = '';
      let suggestion = '';

      switch (check.id) {
        case 'title':
          passed = rule.title.trim().length > 0;
          message = passed ? 'Title is set' : 'Title is missing';
          suggestion = !passed ? 'Add a descriptive title like "Suspicious PowerShell Download Cradle"' : '';
          break;
        case 'description':
          passed = rule.description.trim().length > 0;
          message = passed ? 'Description is set' : 'Description is missing';
          suggestion = !passed ? 'Add a description explaining what this rule detects and why it matters' : '';
          break;
        case 'author':
          passed = rule.author.trim().length > 0;
          message = passed ? 'Author is set' : 'Author is missing';
          suggestion = !passed ? 'Add your name or organization for attribution' : '';
          break;
        case 'logsource':
          passed = rule.logsource.product && rule.logsource.service;
          message = passed ? `Log source: ${rule.logsource.product}/${rule.logsource.service}` : 'Log source incomplete';
          suggestion = !passed ? 'Specify both product (e.g., windows) and service (e.g., security, sysmon)' : '';
          break;
        case 'detection':
          passed = rule.detection.selection.length > 0 && rule.detection.selection.some(s => s.field && s.value);
          message = passed ? `${rule.detection.selection.filter(s => s.field && s.value).length} detection field(s) defined` : 'No detection fields';
          suggestion = !passed ? 'Add at least one detection field like EventID, CommandLine, or Image' : '';
          break;
        case 'condition':
          passed = rule.detection.condition.trim().length > 0;
          message = passed ? `Condition: ${rule.detection.condition}` : 'Condition is missing';
          suggestion = !passed ? 'Set condition like "selection", "selection and not filter", or "selection | count() > 5"' : '';
          break;
        case 'level':
          passed = ['informational', 'low', 'medium', 'high', 'critical'].includes(rule.level);
          message = passed ? `Severity: ${rule.level}` : 'Invalid severity level';
          suggestion = !passed ? 'Choose from: informational, low, medium, high, critical' : '';
          break;
        case 'falsepositives':
          passed = rule.falsepositives.length > 0 && rule.falsepositives.some(fp => fp.trim());
          message = passed ? `${rule.falsepositives.filter(fp => fp.trim()).length} false positive(s) documented` : 'No false positives documented';
          suggestion = !passed ? 'Document known false positives to help analysts triage alerts' : '';
          break;
        default:
          passed = true;
      }

      return { ...check, passed, message, suggestion };
    });

    setPreflightResults(results);
    setShowPreflight(true);

    const failedRequired = results.filter(r => r.required && !r.passed);
    if (failedRequired.length > 0) {
      toast.error(`${failedRequired.length} required check(s) failed`);
    } else {
      toast.success('All required checks passed!');
    }

    return results.filter(r => r.required).every(r => r.passed);
  };

  // Auto-troubleshoot failed checks
  const troubleshootFailedChecks = () => {
    const failed = preflightResults.filter(r => !r.passed);
    const troubleshootSteps = [];

    failed.forEach(check => {
      switch (check.id) {
        case 'title':
          troubleshootSteps.push({
            check: check.name,
            issue: 'Missing rule title',
            autoFix: true,
            action: () => {
              const suggestedTitle = generateSuggestedTitle();
              setRule(prev => ({ ...prev, title: suggestedTitle }));
              return `Auto-generated title: "${suggestedTitle}"`;
            }
          });
          break;
        case 'description':
          troubleshootSteps.push({
            check: check.name,
            issue: 'Missing description',
            autoFix: true,
            action: () => {
              const suggestedDesc = generateSuggestedDescription();
              setRule(prev => ({ ...prev, description: suggestedDesc }));
              return `Auto-generated description`;
            }
          });
          break;
        case 'author':
          troubleshootSteps.push({
            check: check.name,
            issue: 'Missing author',
            autoFix: true,
            action: () => {
              setRule(prev => ({ ...prev, author: 'Security Team' }));
              return 'Set author to "Security Team"';
            }
          });
          break;
        case 'detection':
          troubleshootSteps.push({
            check: check.name,
            issue: 'No detection fields defined',
            autoFix: true,
            action: () => {
              setRule(prev => ({
                ...prev,
                detection: {
                  ...prev.detection,
                  selection: [{ field: 'EventID', modifier: '', value: '4688' }]
                }
              }));
              return 'Added default detection for Process Creation (EventID 4688)';
            }
          });
          break;
        case 'condition':
          troubleshootSteps.push({
            check: check.name,
            issue: 'Missing condition',
            autoFix: true,
            action: () => {
              setRule(prev => ({
                ...prev,
                detection: { ...prev.detection, condition: 'selection' }
              }));
              return 'Set condition to "selection"';
            }
          });
          break;
        case 'level':
          troubleshootSteps.push({
            check: check.name,
            issue: 'Invalid severity level',
            autoFix: true,
            action: () => {
              setRule(prev => ({ ...prev, level: 'medium' }));
              return 'Set severity to "medium"';
            }
          });
          break;
        case 'falsepositives':
          troubleshootSteps.push({
            check: check.name,
            issue: 'No false positives documented',
            autoFix: true,
            action: () => {
              setRule(prev => ({ ...prev, falsepositives: ['Legitimate administrative activity', 'Unknown'] }));
              return 'Added default false positive entries';
            }
          });
          break;
        default:
          troubleshootSteps.push({
            check: check.name,
            issue: check.message,
            autoFix: false,
            action: null
          });
      }
    });

    setTroubleshootResults(troubleshootSteps);
    return troubleshootSteps;
  };

  // Apply all auto-fixes
  const applyAllFixes = () => {
    const results = [];
    troubleshootResults.forEach(step => {
      if (step.autoFix && step.action) {
        const result = step.action();
        results.push({ check: step.check, result });
      }
    });
    
    toast.success(`Applied ${results.length} auto-fix(es)`);
    
    // Re-run preflight after fixes
    setTimeout(() => {
      runPreflightChecks();
    }, 100);
  };

  // Generate suggested title based on detection
  const generateSuggestedTitle = () => {
    const fields = rule.detection.selection.filter(s => s.field && s.value);
    if (fields.length === 0) return 'Suspicious Activity Detection';
    
    const mainField = fields[0];
    if (mainField.field === 'EventID') {
      const eventDescriptions = {
        '4624': 'Successful Logon',
        '4625': 'Failed Logon Attempt',
        '4648': 'Explicit Credential Logon',
        '4672': 'Special Privileges Assigned',
        '4688': 'Process Creation',
        '4689': 'Process Termination',
        '4698': 'Scheduled Task Created',
        '4720': 'User Account Created',
        '4732': 'Member Added to Local Group',
        '1': 'Process Creation (Sysmon)',
        '3': 'Network Connection (Sysmon)',
        '7': 'Image Loaded (Sysmon)',
        '11': 'File Created (Sysmon)',
        '13': 'Registry Value Set (Sysmon)',
      };
      return eventDescriptions[mainField.value] || `Event ${mainField.value} Detection`;
    }
    if (mainField.field === 'CommandLine' || mainField.field === 'Image') {
      return `Suspicious ${mainField.field} Pattern Detection`;
    }
    return `${mainField.field} Based Detection Rule`;
  };

  // Generate suggested description
  const generateSuggestedDescription = () => {
    const fields = rule.detection.selection.filter(s => s.field && s.value);
    if (fields.length === 0) return 'Detects suspicious activity based on defined criteria.';
    
    const fieldDescs = fields.map(f => `${f.field}${f.modifier ? ` (${f.modifier})` : ''}: ${f.value}`).join(', ');
    return `Detects activity matching: ${fieldDescs}. Review for potential security implications.`;
  };

  // Generate Sigma YAML
  const generateSigmaYAML = () => {
    const selectionYaml = rule.detection.selection
      .filter(s => s.field && s.value)
      .map(s => {
        const fieldName = s.modifier ? `${s.field}|${s.modifier}` : s.field;
        // Handle multiple values
        if (s.value.includes(',')) {
          const values = s.value.split(',').map(v => v.trim());
          return `      ${fieldName}:\n${values.map(v => `        - '${v}'`).join('\n')}`;
        }
        return `      ${fieldName}: '${s.value}'`;
      })
      .join('\n');

    const filterYaml = rule.detection.filter.length > 0 
      ? '\n  filter:\n' + rule.detection.filter
          .filter(f => f.field && f.value)
          .map(f => {
            const fieldName = f.modifier ? `${f.field}|${f.modifier}` : f.field;
            return `      ${fieldName}: '${f.value}'`;
          })
          .join('\n')
      : '';

    const refsYaml = rule.references.filter(r => r.trim())
      .map(r => `  - ${r}`).join('\n');

    const tagsYaml = rule.tags.filter(t => t.trim())
      .map(t => `  - ${t}`).join('\n');

    const fpYaml = rule.falsepositives.filter(fp => fp.trim())
      .map(fp => `  - ${fp}`).join('\n');

    return `title: ${rule.title}
id: ${crypto.randomUUID()}
status: ${rule.status}
description: |
  ${rule.description}
author: ${rule.author}
date: ${new Date().toISOString().split('T')[0]}
${refsYaml ? `references:\n${refsYaml}` : ''}
${tagsYaml ? `tags:\n${tagsYaml}` : ''}
logsource:
  product: ${rule.logsource.product}
  service: ${rule.logsource.service}${rule.logsource.category ? `\n  category: ${rule.logsource.category}` : ''}
detection:
  selection:
${selectionYaml}${filterYaml}
  condition: ${rule.detection.condition}
falsepositives:
${fpYaml || '  - Unknown'}
level: ${rule.level}`;
  };

  // Generate KQL Query
  const generateKQLQuery = () => {
    const tableKey = `${rule.logsource.product}-${rule.logsource.service}`;
    const table = LOGSOURCE_TO_KQL_TABLE[tableKey] || 'SecurityEvent';

    const whereConditions = rule.detection.selection
      .filter(s => s.field && s.value)
      .map(s => {
        const kqlField = SIGMA_TO_KQL_FIELDS[s.field] || s.field;
        const values = s.value.split(',').map(v => v.trim());
        
        if (s.modifier === 'contains') {
          if (values.length > 1) {
            return `(${values.map(v => `${kqlField} contains "${v}"`).join(' or ')})`;
          }
          return `${kqlField} contains "${values[0]}"`;
        } else if (s.modifier === 'startswith') {
          if (values.length > 1) {
            return `(${values.map(v => `${kqlField} startswith "${v}"`).join(' or ')})`;
          }
          return `${kqlField} startswith "${values[0]}"`;
        } else if (s.modifier === 'endswith') {
          if (values.length > 1) {
            return `(${values.map(v => `${kqlField} endswith "${v}"`).join(' or ')})`;
          }
          return `${kqlField} endswith "${values[0]}"`;
        } else if (s.modifier === 're') {
          return `${kqlField} matches regex "${values[0]}"`;
        } else {
          // Exact match
          if (values.length > 1) {
            return `${kqlField} in (${values.map(v => `"${v}"`).join(', ')})`;
          }
          return `${kqlField} == "${values[0]}"`;
        }
      });

    const filterConditions = rule.detection.filter
      .filter(f => f.field && f.value)
      .map(f => {
        const kqlField = SIGMA_TO_KQL_FIELDS[f.field] || f.field;
        if (f.modifier === 'contains') {
          return `${kqlField} !contains "${f.value}"`;
        }
        return `${kqlField} != "${f.value}"`;
      });

    let kql = `// KQL Query generated from Sigma Rule: ${rule.title}
// Table: ${table}
// Generated: ${new Date().toISOString()}

${table}
| where TimeGenerated > ago(24h)`;

    if (whereConditions.length > 0) {
      kql += `\n| where ${whereConditions.join('\n    and ')}`;
    }

    if (filterConditions.length > 0) {
      kql += `\n| where ${filterConditions.join('\n    and ')}`;
    }

    // Add aggregation if condition has count
    if (rule.detection.condition.includes('count')) {
      const countMatch = rule.detection.condition.match(/count\(\)\s*>\s*(\d+)/);
      const threshold = countMatch ? countMatch[1] : '5';
      kql += `\n| summarize Count = count() by Computer, Account, bin(TimeGenerated, 1h)
| where Count > ${threshold}`;
    }

    // Add useful projections
    kql += `\n| project TimeGenerated, Computer, Account, EventID, Activity`;
    
    // Add ordering
    kql += `\n| order by TimeGenerated desc
| take 100`;

    return kql;
  };

  // Generate Splunk SPL Query
  const generateSplunkQuery = () => {
    const index = rule.logsource.product === 'windows' ? 'wineventlog' : 'main';
    const sourcetype = rule.logsource.service === 'security' ? 'WinEventLog:Security' : 
                       rule.logsource.service === 'sysmon' ? 'XmlWinEventLog:Microsoft-Windows-Sysmon/Operational' : '*';

    const searchConditions = rule.detection.selection
      .filter(s => s.field && s.value)
      .map(s => {
        const values = s.value.split(',').map(v => v.trim());
        
        if (s.modifier === 'contains') {
          if (values.length > 1) {
            return `(${values.map(v => `${s.field}="*${v}*"`).join(' OR ')})`;
          }
          return `${s.field}="*${values[0]}*"`;
        } else if (s.modifier === 'startswith') {
          if (values.length > 1) {
            return `(${values.map(v => `${s.field}="${v}*"`).join(' OR ')})`;
          }
          return `${s.field}="${values[0]}*"`;
        } else if (s.modifier === 'endswith') {
          if (values.length > 1) {
            return `(${values.map(v => `${s.field}="*${v}"`).join(' OR ')})`;
          }
          return `${s.field}="*${values[0]}"`;
        } else {
          if (values.length > 1) {
            return `(${values.map(v => `${s.field}="${v}"`).join(' OR ')})`;
          }
          return `${s.field}="${values[0]}"`;
        }
      });

    const filterConditions = rule.detection.filter
      .filter(f => f.field && f.value)
      .map(f => `NOT ${f.field}="${f.value}"`);

    let spl = `\`\`\` SPL Query generated from Sigma Rule: ${rule.title} \`\`\`
index=${index} sourcetype="${sourcetype}"`;

    if (searchConditions.length > 0) {
      spl += `\n${searchConditions.join('\n')}`;
    }

    if (filterConditions.length > 0) {
      spl += `\n${filterConditions.join('\n')}`;
    }

    // Add stats if count condition
    if (rule.detection.condition.includes('count')) {
      const countMatch = rule.detection.condition.match(/count\(\)\s*>\s*(\d+)/);
      const threshold = countMatch ? countMatch[1] : '5';
      spl += `\n| stats count by Computer, user, _time span=1h
| where count > ${threshold}`;
    }

    spl += `\n| table _time, Computer, user, EventCode, Message
| head 100`;

    return spl;
  };

  // Generate all queries
  const generateAll = () => {
    const allPassed = runPreflightChecks();
    
    if (!allPassed) {
      toast.error('Please fix required issues before generating');
      return;
    }

    setGeneratedSigma(generateSigmaYAML());
    setGeneratedKQL(generateKQLQuery());
    setGeneratedSplunk(generateSplunkQuery());
    toast.success('All queries generated successfully!');
  };

  // Add detection field
  const addDetectionField = (type = 'selection') => {
    setRule(prev => ({
      ...prev,
      detection: {
        ...prev.detection,
        [type]: [...prev.detection[type], { field: '', modifier: '', value: '' }]
      }
    }));
  };

  // Remove detection field
  const removeDetectionField = (type, index) => {
    setRule(prev => ({
      ...prev,
      detection: {
        ...prev.detection,
        [type]: prev.detection[type].filter((_, i) => i !== index)
      }
    }));
  };

  // Update detection field
  const updateDetectionField = (type, index, key, value) => {
    setRule(prev => ({
      ...prev,
      detection: {
        ...prev.detection,
        [type]: prev.detection[type].map((item, i) => 
          i === index ? { ...item, [key]: value } : item
        )
      }
    }));
  };

  // Copy to clipboard
  const copyToClipboard = (text, name) => {
    navigator.clipboard.writeText(text);
    toast.success(`${name} copied to clipboard`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8 text-indigo-600" />
            Sigma Rule Creator
          </h1>
          <p className="text-gray-500 mt-1">
            Create SIEM detection rules with automatic KQL, SPL, and YAML generation
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runPreflightChecks}>
            <Settings className="w-4 h-4 mr-2" />
            Preflight Check
          </Button>
          <Button onClick={generateAll}>
            <Zap className="w-4 h-4 mr-2" />
            Generate All
          </Button>
        </div>
      </div>

      {/* Preflight Results */}
      {showPreflight && (
        <Card className="border-2 border-indigo-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Preflight Checks
              </CardTitle>
              <div className="flex gap-2">
                {preflightResults.some(r => !r.passed) && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      troubleshootFailedChecks();
                    }}
                  >
                    <Bug className="w-4 h-4 mr-2" />
                    Troubleshoot
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowPreflight(false)}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {preflightResults.map(result => (
                <div 
                  key={result.id}
                  className={`p-3 rounded-lg border ${
                    result.passed 
                      ? 'bg-green-50 border-green-200' 
                      : result.required 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {result.passed ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : result.required ? (
                      <XCircle className="w-4 h-4 text-red-600" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                    )}
                    <span className="font-medium text-sm">{result.name}</span>
                    {result.required && <span className="text-xs text-red-500">*</span>}
                  </div>
                  <p className="text-xs text-gray-600">{result.message}</p>
                </div>
              ))}
            </div>

            {/* Troubleshoot Results */}
            {troubleshootResults.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Troubleshooting Results
                  </h4>
                  <Button size="sm" onClick={applyAllFixes}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Apply All Fixes
                  </Button>
                </div>
                <div className="space-y-2">
                  {troubleshootResults.map((step, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div>
                        <span className="font-medium text-sm">{step.check}:</span>
                        <span className="text-sm text-gray-600 ml-2">{step.issue}</span>
                      </div>
                      {step.autoFix ? (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            const result = step.action();
                            toast.success(result);
                            runPreflightChecks();
                          }}
                        >
                          Auto-Fix
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">Manual fix required</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rule Builder */}
        <div className="space-y-4">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Rule Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={rule.title}
                  onChange={(e) => setRule({ ...rule, title: e.target.value })}
                  placeholder="e.g., Suspicious PowerShell Download Cradle"
                />
              </div>
              <div>
                <Label>Description</Label>
                <textarea
                  value={rule.description}
                  onChange={(e) => setRule({ ...rule, description: e.target.value })}
                  placeholder="Describe what this rule detects..."
                  className="w-full h-20 p-3 border rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Author</Label>
                  <Input
                    value={rule.author}
                    onChange={(e) => setRule({ ...rule, author: e.target.value })}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label>Severity Level</Label>
                  <select
                    value={rule.level}
                    onChange={(e) => setRule({ ...rule, level: e.target.value })}
                    className="w-full h-10 border rounded-lg px-3"
                  >
                    <option value="informational">Informational</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Log Source */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Log Source</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Product *</Label>
                  <select
                    value={rule.logsource.product}
                    onChange={(e) => setRule({ ...rule, logsource: { ...rule.logsource, product: e.target.value } })}
                    className="w-full h-10 border rounded-lg px-3"
                  >
                    <option value="windows">Windows</option>
                    <option value="linux">Linux</option>
                    <option value="macos">macOS</option>
                    <option value="azure">Azure</option>
                    <option value="aws">AWS</option>
                    <option value="gcp">GCP</option>
                  </select>
                </div>
                <div>
                  <Label>Service *</Label>
                  <select
                    value={rule.logsource.service}
                    onChange={(e) => setRule({ ...rule, logsource: { ...rule.logsource, service: e.target.value } })}
                    className="w-full h-10 border rounded-lg px-3"
                  >
                    <option value="security">Security</option>
                    <option value="sysmon">Sysmon</option>
                    <option value="powershell">PowerShell</option>
                    <option value="system">System</option>
                    <option value="application">Application</option>
                    <option value="dns">DNS</option>
                    <option value="firewall">Firewall</option>
                    <option value="proxy">Proxy</option>
                  </select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Input
                    value={rule.logsource.category}
                    onChange={(e) => setRule({ ...rule, logsource: { ...rule.logsource, category: e.target.value } })}
                    placeholder="process_creation"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detection */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Detection Logic</CardTitle>
                <Button size="sm" variant="outline" onClick={() => addDetectionField('selection')}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Field
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-2 block">Selection Fields (match any)</Label>
                {rule.detection.selection.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <select
                      value={item.field}
                      onChange={(e) => updateDetectionField('selection', idx, 'field', e.target.value)}
                      className="w-1/3 h-9 border rounded px-2 text-sm"
                    >
                      <option value="">Select field...</option>
                      <option value="EventID">EventID</option>
                      <option value="CommandLine">CommandLine</option>
                      <option value="ParentCommandLine">ParentCommandLine</option>
                      <option value="Image">Image (Process Path)</option>
                      <option value="ParentImage">ParentImage</option>
                      <option value="User">User</option>
                      <option value="TargetUserName">TargetUserName</option>
                      <option value="LogonType">LogonType</option>
                      <option value="SourceIp">SourceIp</option>
                      <option value="DestinationIp">DestinationIp</option>
                      <option value="DestinationPort">DestinationPort</option>
                      <option value="Hashes">Hashes</option>
                      <option value="TargetFilename">TargetFilename</option>
                      <option value="TargetObject">TargetObject (Registry)</option>
                      <option value="QueryName">QueryName (DNS)</option>
                    </select>
                    <select
                      value={item.modifier}
                      onChange={(e) => updateDetectionField('selection', idx, 'modifier', e.target.value)}
                      className="w-1/5 h-9 border rounded px-2 text-sm"
                    >
                      <option value="">Exact</option>
                      <option value="contains">Contains</option>
                      <option value="startswith">Starts with</option>
                      <option value="endswith">Ends with</option>
                      <option value="re">Regex</option>
                    </select>
                    <Input
                      value={item.value}
                      onChange={(e) => updateDetectionField('selection', idx, 'value', e.target.value)}
                      placeholder="Value (comma for multiple)"
                      className="flex-1 h-9"
                    />
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => removeDetectionField('selection', idx)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Filter (exclusions) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Filter (Exclusions)</Label>
                  <Button size="sm" variant="ghost" onClick={() => addDetectionField('filter')}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                {rule.detection.filter.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <select
                      value={item.field}
                      onChange={(e) => updateDetectionField('filter', idx, 'field', e.target.value)}
                      className="w-1/3 h-9 border rounded px-2 text-sm"
                    >
                      <option value="">Select field...</option>
                      <option value="Image">Image</option>
                      <option value="User">User</option>
                      <option value="CommandLine">CommandLine</option>
                      <option value="ParentImage">ParentImage</option>
                    </select>
                    <select
                      value={item.modifier}
                      onChange={(e) => updateDetectionField('filter', idx, 'modifier', e.target.value)}
                      className="w-1/5 h-9 border rounded px-2 text-sm"
                    >
                      <option value="">Exact</option>
                      <option value="contains">Contains</option>
                    </select>
                    <Input
                      value={item.value}
                      onChange={(e) => updateDetectionField('filter', idx, 'value', e.target.value)}
                      placeholder="Value to exclude"
                      className="flex-1 h-9"
                    />
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => removeDetectionField('filter', idx)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div>
                <Label>Condition</Label>
                <Input
                  value={rule.detection.condition}
                  onChange={(e) => setRule({ 
                    ...rule, 
                    detection: { ...rule.detection, condition: e.target.value } 
                  })}
                  placeholder="selection and not filter"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Examples: selection, selection and not filter, selection | count() &gt; 5
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">MITRE ATT&CK Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {['attack.execution', 'attack.persistence', 'attack.privilege_escalation', 
                  'attack.defense_evasion', 'attack.credential_access', 'attack.discovery',
                  'attack.lateral_movement', 'attack.collection', 'attack.exfiltration',
                  'attack.command_and_control', 'attack.initial_access'].map(tag => (
                  <Button
                    key={tag}
                    variant={rule.tags.includes(tag) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setRule(prev => ({
                        ...prev,
                        tags: prev.tags.includes(tag)
                          ? prev.tags.filter(t => t !== tag)
                          : [...prev.tags, tag]
                      }));
                    }}
                  >
                    {tag.replace('attack.', '')}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Generated Output */}
        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="text-lg">Generated Queries</CardTitle>
              <CardDescription>
                Click on a tab to view the generated query format
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="sigma" className="flex items-center gap-1">
                    <FileCode className="w-4 h-4" />
                    Sigma YAML
                  </TabsTrigger>
                  <TabsTrigger value="kql" className="flex items-center gap-1">
                    <Database className="w-4 h-4" />
                    KQL (Azure)
                  </TabsTrigger>
                  <TabsTrigger value="splunk" className="flex items-center gap-1">
                    <Terminal className="w-4 h-4" />
                    Splunk SPL
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="sigma" className="mt-4">
                  {generatedSigma ? (
                    <div>
                      <div className="flex justify-end mb-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(generatedSigma, 'Sigma YAML')}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono max-h-[500px]">
                        {generatedSigma}
                      </pre>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <FileCode className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Click "Generate All" to create Sigma YAML</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="kql" className="mt-4">
                  {generatedKQL ? (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-500">
                          For Azure Sentinel / Microsoft Defender
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(generatedKQL, 'KQL Query')}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <pre className="bg-[#1e1e1e] text-[#d4d4d4] p-4 rounded-lg overflow-auto text-sm font-mono max-h-[500px]">
                        <code>
                          {generatedKQL.split('\n').map((line, i) => (
                            <div key={i} className={
                              line.startsWith('//') ? 'text-[#6a9955]' :
                              line.startsWith('|') ? 'text-[#569cd6]' :
                              line.includes('where') || line.includes('project') || line.includes('summarize') ? 'text-[#c586c0]' :
                              ''
                            }>
                              {line}
                            </div>
                          ))}
                        </code>
                      </pre>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Click "Generate All" to create KQL query</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="splunk" className="mt-4">
                  {generatedSplunk ? (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-500">
                          For Splunk Enterprise / Cloud
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(generatedSplunk, 'Splunk SPL')}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <pre className="bg-[#000] text-[#00ff00] p-4 rounded-lg overflow-auto text-sm font-mono max-h-[500px]">
                        {generatedSplunk}
                      </pre>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Terminal className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Click "Generate All" to create Splunk query</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SigmaRules;
